import { useMemo } from 'react';

import { Skeleton, Stack, Typography } from '@mui/material';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';

import Loader from 'src/components/Loader/Loader';
import {
    NPIDataGrid,
    NPIDataGridColDef,
    NPIDataGridRow,
} from 'src/components/NPIDataGrid/NPIDataGrid';
import { resolvePageSize } from 'src/lib/pagination';

export type SepaAgreementTableProps = {
    tableRowData: NPIDataGridRow[];
    tableColDefs: NPIDataGridColDef[];
    loadingTableRowData: boolean;
    loadingAgreementDocumentPdf: boolean;
    title: string;
    dataTestId: string;
    onFilterChange: (model: GridFilterModel) => void | null;
    filterState?: GridFilterModel;
    serverSideSorting?: {
        onSortModelChange: (sortModel: GridSortModel) => void;
        sortModel?: GridSortModel;
    } | null;
    serverSidePagination?: {
        pageSizeOptions?: number[];
        mode: 'client' | 'server';
        page: number;
        pageSize: number;
        totalRows: number;
        onPageChange: (page: number, pageSize: number) => void;
    };
    handleReset?: () => void;
};

export function SepaAgreementTable({
    tableRowData,
    tableColDefs,
    loadingAgreementDocumentPdf,
    title,
    loadingTableRowData,
    dataTestId,
    onFilterChange = null,
    filterState = null,
    serverSideSorting = null,
    serverSidePagination,
    handleReset,
}: SepaAgreementTableProps) {
    const serverSideFilteringConfig = useMemo(() => {
        if (!onFilterChange) return null;
        return {
            onFilterChange,
            filterModel: filterState,
        };
    }, [onFilterChange, filterState]);

    const { mode, page, pageSize, totalRows, onPageChange } = serverSidePagination ?? {
        mode: 'client' as const,
        page: 1,
        pageSize: 5,
        totalRows: 0,
        onPageChange: () => {},
    };

    return (
        <Stack>
            <Typography variant="h3" paddingY={3}>
                {title}
            </Typography>
            {loadingAgreementDocumentPdf && <Loader />}
            {loadingTableRowData ? (
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    placeholder={`Loading ${title}`}
                    width={'100%'}
                    height={'400px'}
                />
            ) : (
                <NPIDataGrid
                    handleReset={handleReset}
                    dataTestId={dataTestId}
                    rows={tableRowData}
                    columns={tableColDefs}
                    getRowId={(row) => {
                        return row.id;
                    }}
                    serverSideFiltering={serverSideFilteringConfig}
                    serverSideSorting={{
                        onSortChange: serverSideSorting?.onSortModelChange,
                        sortModel: serverSideSorting?.sortModel ?? [],
                    }}
                    pagination={{
                        isEnabled: true,
                        mode,
                        page,
                        pageSize: resolvePageSize(pageSize),
                        totalRows,
                        onPageChange,
                    }}
                />
            )}
        </Stack>
    );
}
